import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { AutoPauseVideo } from '../Generic/AutoPauseVideo';
import { SimpleTestimonial } from '../PageSections/HalfBGContentCard';
import PageSection from '../PageSections/PageSection';
import * as styles from './ContentCard.module.scss';
import ContentfulImage from './ContentfulImage';
import { ArrowLink } from './Links';
import RichTextConverter from './RichText/RichTextConverter';

const ContentCard = ({
  eyebrowText,
  eyebrowColor,
  heading,
  logo,
  copy,
  anchorImage,
  image,
  wistiaUrl,
  imageBleed,
  imageSide,
  imageHeight,
  imageWidth,
  ctaLabel,
  ctaUrl,
  newWindow,
  limitImageHeight,
  backgroundStyle,
  backgroundImage,
  attachedContent,
  useH3 = false,
  adjustForTallImage = false,
  ctaIsDownload = false,
  slim = false,
  moderate = false,
  curve = '',
  className = '',
  animate = false,
  lazy = false,
  fullHeight = false,
  extraWide = false,
  useObject = false,
  setPageBackground,
  themeClass,
}) => {
  // const isSvg = image?.file?.contentType === 'image/svg+xml';
  // let heightStyle = styles.free;
  // if (adjustForTallImage && !isSvg) {
  //   heightStyle = styles.heightAdjust;
  // } else if (adjustForTallImage && isSvg) {
  //   heightStyle = styles.svgLimitHeight;
  // } else if (isSvg) {
  //   heightStyle = styles.svgHeight;
  // }
  const { ref: containerRef, inView: isVisible } = useInView({
    threshold: fullHeight ? 0.5 : 0.25,
    triggerOnce: !fullHeight,
  });
  useEffect(() => {
    if (isVisible && backgroundImage && setPageBackground) setPageBackground(`url('${backgroundImage.file?.url}')`);
  }, [isVisible]);

  const shrinkHeading = heading?.length > 70 ? true : false;
  const imageDirection = imageSide === 'Left' ? 'Left' : 'Right';
  const animatable = animate && !fullHeight;
  const imgStyle = { objectFit: 'contain', maxHeight: '750px' };
  if (limitImageHeight) {
    imgStyle.maxHeight = '300px';
  }
  let mainMedia;
  if (image) {
    //mainMedia = <img className={styles.heightAdjust} src={get(image, 'file.url')} alt={get(image, 'title')} />;

    if (useObject && image.file.contentType === 'image/svg+xml')
      mainMedia = (
        <div className={styles.svgObject}>
          <object aria-label={heading} data={image.file.url} type="image/svg+xml" />
        </div>
      );
    else
      mainMedia = (
        <ContentfulImage
          lazy={lazy}
          imageFragment={image}
          className={styles.svg}
          height={imageHeight}
          width={imageWidth}
        />
      );
  }

  const isCopyRaw = typeof copy !== 'string';
  return (
    <PageSection
      wide={imageBleed}
      fullBleed={extraWide}
      className={[
        backgroundStyle,
        anchorImage ? styles.anchorImage : '',
        fullHeight ? styles.fullHeight : '',
        extraWide ? styles.extraWide : '',
        className,
        themeClass,
      ].join(' ')}
      curveTop={curve === 'Top'}
      curveBottom={curve === 'Bottom'}
      slim={slim}
      moderate={moderate}
      backgroundImage={fullHeight ? null : backgroundImage}
      data-analytics-component={'content-card'}
      data-analytics-id={heading}
      style={{ overflow: 'hidden', opacity: fullHeight ? (isVisible ? 1 : 0) : 1 }}
    >
      <div className={[styles.grid, imageBleed ? styles.wide : ''].join(' ')} ref={containerRef}>
        {wistiaUrl ? (
          <div
            className={[
              styles.videoWrapper,
              animatable ? styles['slide' + imageDirection] : '',
              isVisible && animatable ? styles.visible : '',
            ].join(' ')}
          >
            <AutoPauseVideo
              classes={[`react-player-${wistiaUrl}`, styles.videoPlayer]}
              controls={true}
              src={wistiaUrl}
            />
          </div>
        ) : (
          <div
            className={[
              styles['image' + imageSide],
              limitImageHeight ? styles.minWidth : '',
              adjustForTallImage ? styles.imageAdjust : '',
              anchorImage ? styles.anchorImageGrid : '',
              animatable ? styles['slide' + imageDirection] : '',
              isVisible && animatable ? styles.visible : '',
            ].join(' ')}
          >
            {mainMedia}
          </div>
        )}
        <div
          data-analytics-component={'content-card-copy'}
          data-analytics-id={heading}
          className={[
            styles['copy' + imageSide],
            imageBleed ? styles.padAdjust : '',
            animatable ? styles.hidden : '',
            isVisible && animatable ? styles.fadeUp : '',
          ].join(' ')}
        >
          {eyebrowText && <div className={[styles.eyebrowText, styles[eyebrowColor]].join(' ')}>{eyebrowText}</div>}
          <h2 className={[styles.heading, useH3 || shrinkHeading ? styles.h3Styles : ''].join(' ')}>{heading}</h2>
          {logo && <ContentfulImage imageFragment={logo} className={styles.logo} />}
          {isCopyRaw && <RichTextConverter copy={copy} />}
          {!isCopyRaw && <p>{copy}</p>}
          {attachedContent?.__typename === 'ContentfulComponentTestimonial' && (
            <SimpleTestimonial testimonial={attachedContent} />
          )}
          {ctaLabel && <ArrowLink label={ctaLabel} url={ctaUrl} isExternal={newWindow} isDownload={ctaIsDownload} />}
        </div>
      </div>
    </PageSection>
  );
};

ContentCard.propTypes = {
  heading: PropTypes.string,
  copy: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  image: PropTypes.object,
  imageBleed: PropTypes.bool,
  imageSide: PropTypes.oneOf(['Left', 'Right']),
  ctaLabel: PropTypes.string,
  ctaUrl: PropTypes.string,
  curve: PropTypes.oneOf(['Top', 'Bottom', 'No']),
  slim: PropTypes.bool,
};

export default ContentCard;
